/**
 * Template Library Component
 *
 * Displays a grid of available templates that users can import.
 *
 * @package TopBuddy
 */

import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Button, Spinner } from '@wordpress/components';
import { useDispatch } from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';

const TemplateLibrary = ({ onImport, onClose }) => {
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [importing, setImporting] = useState(null);
	const [error, setError] = useState(null);

	const { setActiveComponents } = useDispatch('topbuddy/components');
	const { setSettings } = useDispatch('topbuddy/settings');
	const { setDisplayRules } = useDispatch('topbuddy/displayRules');

	useEffect(() => {
		fetchTemplates();
	}, []);

	const fetchTemplates = async () => {
		try {
			setLoading(true);
			const response = await apiFetch({
				path: '/topbuddy/v1/templates',
			});
			setTemplates(response.templates || []);
			setError(null);
		} catch (err) {
			setError(__('Failed to load templates. Please try again.', 'topbuddy'));
			console.error('Error fetching templates:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleImport = async (template) => {
		// Show confirmation dialog
		const confirmed = window.confirm(
			__('Importing this template will replace your current components and settings. Are you sure you want to continue?', 'topbuddy')
		);

		if (!confirmed) {
			return;
		}

		try {
			setImporting(template.id);
			setError(null);

			// Get current post ID
			const postId = window.topbuddyPostSettings?.postId || wp.data.select('core/editor')?.getCurrentPostId();

			if (!postId) {
				throw new Error(__('Unable to determine post ID', 'topbuddy'));
			}

			// Call import API
			const response = await apiFetch({
				path: '/topbuddy/v1/templates/import',
				method: 'POST',
				data: {
					template_id: template.id,
					post_id: postId,
				},
			});

			if (response.success) {
				// Update React stores with imported data
				if (response.components) {
					setActiveComponents(response.components);
				}
				if (response.settings) {
					setSettings(response.settings);
				}
				if (response.display_rules) {
					setDisplayRules(response.display_rules);
				}

				// Show success message
				if (wp.data.dispatch('core/notices')) {
					wp.data.dispatch('core/notices').createNotice(
						'success',
						__('Template imported successfully!', 'topbuddy'),
						{
							type: 'snackbar',
							isDismissible: true,
						}
					);
				}

				// Call onImport callback
				if (onImport) {
					onImport(template);
				}

				// Close modal after a short delay
				setTimeout(() => {
					if (onClose) {
						onClose();
					}
				}, 500);
			}
		} catch (err) {
			setError(err.message || __('Failed to import template. Please try again.', 'topbuddy'));
			console.error('Error importing template:', err);
			
			// Show error notice
			if (wp.data.dispatch('core/notices')) {
				wp.data.dispatch('core/notices').createNotice(
					'error',
					err.message || __('Failed to import template.', 'topbuddy'),
					{
						type: 'snackbar',
						isDismissible: true,
					}
				);
			}
		} finally {
			setImporting(null);
		}
	};

	if (loading) {
		return (
			<div className="topbuddy-template-library-loading">
				<Spinner />
				<p>{__('Loading templates...', 'topbuddy')}</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="topbuddy-template-library-error">
				<p>{error}</p>
				<Button variant="secondary" onClick={fetchTemplates}>
					{__('Retry', 'topbuddy')}
				</Button>
			</div>
		);
	}

	if (templates.length === 0) {
		return (
			<div className="topbuddy-template-library-empty">
				<p>{__('No templates available at the moment.', 'topbuddy')}</p>
			</div>
		);
	}

	return (
		<div className="topbuddy-template-library">
			<div className="topbuddy-template-library-header">
				<h3>{__('Choose a Template', 'topbuddy')}</h3>
				<p className="description">
					{__('Select a premade design to get started quickly.', 'topbuddy')}
				</p>
			</div>
			<div className="topbuddy-template-library-grid">
				{templates.map((template) => (
					<div key={template.id} className="topbuddy-template-card">
						<div className="topbuddy-template-card-preview">
							{template.thumbnail ? (
								<img src={template.thumbnail} alt={template.name} />
							) : (
								<div className="topbuddy-template-card-placeholder">
									<span className="dashicons dashicons-format-gallery"></span>
								</div>
							)}
						</div>
						<div className="topbuddy-template-card-content">
							<h4 className="topbuddy-template-card-title">
								{template.name}
							</h4>
						</div>
						<div className="topbuddy-template-card-actions">
							<Button
								variant="primary"
								onClick={() => handleImport(template)}
								disabled={importing !== null}
								isBusy={importing === template.id}
							>
								{importing === template.id 
									? __('Importing...', 'topbuddy')
									: __('Import', 'topbuddy')
								}
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default TemplateLibrary;
